import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import '../../Styles/Layout/CustomSelect.css';

interface Option {
  value: string;
  label: string;
  icon?: React.ComponentType<any>;
}

interface CustomSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: React.ComponentType<any>;
  renderOption?: (option: Option) => React.ReactNode;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select option',
  icon: Icon,
  renderOption,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: Option) => {
    onChange(option.value);
    setIsOpen(false);
  };

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className="custom-select-container">
      <div className="custom-select" ref={selectRef}>
        <button
          className={`select-button ${isOpen ? 'open' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          type="button"
        >
          <div className="select-button-content">
            {Icon && (
              <div className="icon-with-select">
                <Icon size={18} />
              </div>
            )}
            <span>{selectedOption ? selectedOption.label : placeholder}</span>
          </div>
          <div className="select-button-icon">
            <ChevronDown size={16} aria-hidden />
          </div>
        </button>
        <div className={`select-options ${isOpen ? 'open' : ''}`}>
          {options.map((option) => (
            <div
              key={option.value}
              className={`select-option ${option.value === value ? 'selected' : ''}`}
              onClick={() => handleSelect(option)}
            >
              {renderOption ? (
                renderOption(option)
              ) : (
                <>
                  {option.icon && <option.icon size={18} />}
                  <span>{option.label}</span>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CustomSelect;